'use client';

import React, { useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Pagination, Navigation, Autoplay } from 'swiper/modules';
// 使用模块化CSS导入，减少bundle大小
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/keyboard';
import 'swiper/css/autoplay';
import styles from './CarSwiper.module.scss';
import cn from 'classnames';

import Button from '../../../../../../src/components/pages/ui/Button/Button';

import { useMediaQuery } from 'usehooks-ts';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import OptimizedImage from '../../../../ui/OptimizedImage/OptimizedImage';
import leftArrow from '../../../../../../src/assets/icons/left-arrow-black.svg';
import rightArrow from '../../../../../../src/assets/icons/right-arrow-black.svg';

export default function CarSwiper({ data, buttonConfig }) {
  const isTablet = useMediaQuery('(max-width: 834px)');
  const isMobile = useMediaQuery('(max-width: 480px)');
  const ButtonNextId = `carSwiperButtonNext_${new Date().getTime()}`;
  const getImageUrl = (item) => {
    // 检查是否是 Sitecore 数据结构
    if (item && item.fields) {
      // 使用老项目的 getImageUrl 逻辑
      const getImageUrlFromField = (imageField) => {
        if (!imageField?.fields) return '';
        return (
          imageField.fields.desktopImage?.value?.src ||
          imageField.fields.tabletImage?.value?.src ||
          imageField.fields.mobileImage?.value?.src ||
          ''
        );
      };

      // 根据设备类型获取对应的图片
      if (isMobile) {
        // 移动端：优先使用 mobileImage，然后是 tabImage，最后是 image
        const mobileImageUrl = getImageUrlFromField(item.fields.mobileImage);
        const tabImageUrl = getImageUrlFromField(item.fields.tabImage);
        const desktopImageUrl = getImageUrlFromField(item.fields.image);

        return (
          mobileImageUrl ||
          tabImageUrl ||
          desktopImageUrl ||
          buttonConfig?.defaultHeroImage ||
          '/public/images/car-sunset-road.png'
        );
      }

      if (isTablet) {
        // 平板端：优先使用 tabImage，然后是 image
        const tabImageUrl = getImageUrlFromField(item.fields.tabImage);
        const desktopImageUrl = getImageUrlFromField(item.fields.image);

        return (
          tabImageUrl ||
          desktopImageUrl ||
          buttonConfig?.defaultHeroImage ||
          '/public/images/car-sunset-road.png'
        );
      }

      // 桌面端：使用 image
      const desktopImageUrl = getImageUrlFromField(item.fields.image);

      return (
        desktopImageUrl || buttonConfig?.defaultHeroImage || '/public/images/car-sunset-road.png'
      );
    }

    // 如果是旧的数据结构
    if (isMobile) {
      return (
        item.mobileImage?.url ||
        item.tabImage?.url ||
        item.image?.url ||
        buttonConfig?.defaultHeroImage ||
        '/public/images/car-sunset-road.png'
      );
    }
    if (isTablet) {
      return (
        item.tabImage?.url ||
        item.image?.url ||
        buttonConfig?.defaultHeroImage ||
        '/public/images/car-sunset-road.png'
      );
    }
    return (
      item.image?.url || buttonConfig?.defaultHeroImage || '/public/images/car-sunset-road.png'
    );
  };

  const getText = (item) => {
    const defaultText = buttonConfig?.defaultHeroText || 'Welcome to Jaecoo';
    if (item && item.fields) {
      return item.fields.text?.value || defaultText;
    }
    return item.text || defaultText;
  };

  const getAlign = (item) => {
    let result = '';

    if (item && item.fields) {
      result = item.fields.align?.value || '';
    }

    return result;
  };

  const getAlignCol = (item) => {
    let result = '';

    if (item && item.fields) {
      result = item.fields.alignCol?.value || '';
    }

    return result;
  };

  const getAlignPc = (item) => {
    let result = '';

    if (item && item.fields) {
      result = item.fields.alignPc?.value || '';
    }

    return result;
  };

  const getAlignColPc = (item) => {
    let result = '';

    if (item && item.fields) {
      result = item.fields.alignColPc?.value || '';
    }

    return result;
  };

  const getButton = (item) => {
    if (item && item.fields) {
      const buttonPath = item.fields.buttonPath?.value;
      const buttonText = item.fields.buttonText?.value;
      const query = buttonPath?.querystring || '';

      if (buttonPath && buttonText) {
        return {
          path:
            buttonPath.href || buttonPath.url || buttonConfig?.defaultButtonPath
              ? (buttonPath.href || buttonPath.url || buttonConfig?.defaultButtonPath) + query
              : '#',
          text: buttonText,
          target: buttonPath.target || '_self',
        };
      }
    }
    return item.homeHeroButton || null;
  };

  const getButton2 = (item) => {
    if (item && item.fields) {
      const buttonPath = item.fields.buttonPath2?.value;
      const buttonText = item.fields.buttonText2?.value;
      const query = buttonPath?.querystring || '';

      if (buttonPath && buttonText) {
        return {
          path:
            buttonPath.href || buttonPath.url || buttonConfig?.defaultButtonPath
              ? (buttonPath.href || buttonPath.url || buttonConfig?.defaultButtonPath) + query
              : '#',
          text: buttonText,
          target: buttonPath.target || '_self',
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  // 预加载Hero图片以改善LCP
  useEffect(() => {
    if (typeof window !== 'undefined' && data && data.length > 0) {
      // 立即预加载第一张图片（LCP候选）
      const firstImageUrl = getImageUrl(data[0]);
      if (firstImageUrl) {
        // 使用link preload进行高优先级预加载
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.href = firstImageUrl;
        preloadLink.fetchPriority = 'high';
        document.head.appendChild(preloadLink);

        // 同时使用Image对象预加载
        const img = new window.Image();
        img.src = firstImageUrl;
        img.loading = 'eager';
        img.fetchPriority = 'high';
      }

      // 预加载其他图片
      data.forEach((item, index) => {
        if (index > 0) {
          const url = getImageUrl(item);
          if (url) {
            const img = new window.Image();
            img.src = url;
            img.loading = 'lazy';
          }
        }
      });
    }
  }, [data, isMobile, isTablet]);

  return (
    <div className={styles.swiperContainer} id="swiperContainerGallery">
      <Swiper
        slidesPerView={1}
        spaceBetween={30}
        loop={true}
        keyboard={{ enabled: true }}
        pagination={{
          el: '#paginationId',
          clickable: true,
        }}
        navigation={{
          nextEl: `#${ButtonNextId}`,
          prevEl: `.${styles.swiperButtonPrev}`,
        }}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
        }}
        modules={[Keyboard, Pagination, Navigation, Autoplay]}
        className={styles.swiper}
      >
        {(data || []).map((item, index) => {
          const imageUrl = getImageUrl(item);
          const text = getText(item);
          const button = getButton(item);
          const button2 = getButton2(item);
          const align = getAlign(item);
          const alignPc = getAlignPc(item);
          const alignCol = getAlignCol(item);
          const alignColPc = getAlignColPc(item);

          const classAll =
            styles.textBlock +
            ' ' +
            (alignPc ? styles[alignPc + '_CsPc'] : '') +
            ' ' +
            (alignColPc ? styles[alignColPc + '_col_CsPc'] : '') +
            ' ' +
            (align ? styles[align + '_CsMb'] : '') +
            ' ' +
            (alignCol ? styles[alignCol + '_col_CsMb'] : '');

          // 根据设备类型确定Hero图片配置
          const getHeroImageConfig = () => {
            if (isMobile) {
              return {
                width: 480, // 移动端进一步优化
                height: 320,
                device: 'mobile',
                quality: 60, // 移动端使用更低质量以加快LCP
              };
            } else if (isTablet) {
              return {
                width: 800,
                height: 500,
                device: 'tablet',
                quality: 75,
              };
            } else {
              return {
                width: 1080,
                height: 560,
                device: 'hero',
                quality: 80,
              };
            }
          };

          const heroConfig = getHeroImageConfig();

          return (
            <SwiperSlide key={index}>
              <div className={styles.slide}>
                <OptimizedImage
                  src={imageUrl}
                  alt=""
                  width={heroConfig.width}
                  height={heroConfig.height}
                  priority={index === 0} // 首张图片高优先级
                  loading={index === 0 ? 'eager' : 'lazy'}
                  quality={heroConfig.quality}
                  device={heroConfig.device}
                  objectFit="cover"
                  className={styles.slideImage}
                />

                <div className={classAll}>
                  <div className={styles.title}>
                    <p dangerouslySetInnerHTML={{ __html: text }} />
                  </div>
                  {(button || button2) && (
                    <div className={styles.buttonWrapper}>
                      {button && (
                        <Button
                          className={styles.button}
                          isShowTooltip={false}
                          target={button.path}
                        >
                          {button.text}
                        </Button>
                      )}

                      {button2 && (
                        <Button
                          className={styles.button}
                          isShowTooltip={false}
                          target={button2.path}
                        >
                          {button2.text}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className={styles.controls}>
        <div className={cn(styles.swiperButtonPrev, styles.mobile)}>
          <Image
            field={{
              value: {
                src: leftArrow,
                alt: '',
                width: 14,
                height: 14,
              },
            }}
          />
        </div>
        <div className={cn(styles.myPagination, styles.mobile)} id="paginationId" />
        <div className={cn(styles.swiperButtonNext, styles.mobile)} id={ButtonNextId}>
          <Image
            field={{
              value: {
                src: rightArrow,
                alt: '',
                width: 14,
                height: 14,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
